import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faPlus, faTrash, faDatabase } from '@fortawesome/free-solid-svg-icons';
import { SectionTitle, Input, SaveButton, AddButton, RemoveButton, InputGroup, Checkbox } from './styled';

const RelationTableSettings = ({ 
  tableName, 
  values, 
  setValues, 
  handleSaveActionInfo 
}) => {
  const [availableTables, setAvailableTables] = useState([]);
  const [cteDefinitions, setCteDefinitions] = useState([]);

  // 컴포넌트 마운트 시 메인 테이블 기본값 설정
  useEffect(() => {
    if (tableName && (!values.save?.table || values.save.table === '')) {
      setValues(prev => ({
        ...prev,
        save: { ...prev.save, table: tableName },
        update: { ...prev.update, table: tableName },
        delete: { ...prev.delete, table: tableName }
      }));
    }
  }, [tableName, values.save?.table, setValues]);

  // 사용 가능한 테이블 목록 조회
  useEffect(() => {
    const fetchAvailableTables = async () => {
      try {
        const response = await fetch('/request/getTableList', {
          credentials: 'include'
        });
        const data = await response.json();
        if (Array.isArray(data)) {
          setAvailableTables(data);
        } else {
          setAvailableTables([]);
        }
      } catch (error) {
        console.error('Error fetching table list:', error);
        setAvailableTables([]);
      }
    };

    fetchAvailableTables();
  }, []);

  const handleChange = (field, value) => {
    setValues(prev => ({
      ...prev,
      save: { ...prev.save, [field]: value },
      update: { ...prev.update, [field]: value },
      delete: { ...prev.delete, [field]: value }
    }));
  };

  const handleArrayChange = (actionType, field, index, value) => {
    setValues(prev => {
      const currentArray = prev[actionType]?.[field] || [];
      const newArray = currentArray.map((item, i) => i === index ? value : item);
      
      return {
        ...prev,
        save: { ...prev.save, [field]: newArray },
        update: { ...prev.update, [field]: newArray },
        delete: { ...prev.delete, [field]: newArray }
      };
    });
  };

  const handleAddField = (actionType, field) => {
    setValues(prev => ({
      ...prev,
      [actionType]: {
        ...prev[actionType],
        [field]: [...(prev[actionType]?.[field] || []), ""]
      }
    }));
  };

  const handleRemoveField = (actionType, field, index) => {
    setValues(prev => ({
      ...prev,
      [actionType]: {
        ...prev[actionType],
        [field]: (prev[actionType]?.[field] || []).filter((_, i) => i !== index)
      }
    }));
  };

  // CTE 관련 함수들
  const handleAddCTE = () => {
    setCteDefinitions(prev => [...prev, {
      name: '',
      query: ''
    }]);
  };

  const handleRemoveCTE = (index) => {
    setCteDefinitions(prev => prev.filter((_, i) => i !== index));
  };

  const handleCTEChange = (index, field, value) => {
    setCteDefinitions(prev => 
      prev.map((cte, i) => 
        i === index ? { ...cte, [field]: value } : cte
      )
    );
  };

  const renderInputs = (actionType, field) => (
    <>
      {(values[actionType]?.[field] || []).map((value, index) => (
        <InputGroup key={index}>
          <Input
            value={value || ''}
            onChange={(e) => handleArrayChange(actionType, field, index, e.target.value)}
            placeholder={`${field} ${index + 1}`}
          />
          <RemoveButton onClick={() => handleRemoveField(actionType, field, index)}>
            <FontAwesomeIcon icon={faTrash} />
          </RemoveButton>
        </InputGroup>
      ))}
      <AddButton onClick={() => handleAddField(actionType, field)}>
        <FontAwesomeIcon icon={faPlus} /> Add {field}
      </AddButton>
    </>
  );

  // 콤보박스에서 테이블 선택해서 추가하는 함수
  const handleTableSelectChange = (e) => {
    const selectedTable = e.target.value;
    if (!selectedTable) return;
    
    const currentTables = values.save?.relationTables || [];
    if (!currentTables.includes(selectedTable)) {
      setValues(prev => ({
        ...prev,
        save: { ...prev.save, relationTables: [...currentTables, selectedTable] },
        update: { ...prev.update, relationTables: [...currentTables, selectedTable] },
        delete: { ...prev.delete, relationTables: [...currentTables, selectedTable] }
      }));
    }
    // 선택 후 콤보박스 초기화
    e.target.value = '';
  };

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      width: 'calc(100% - 40px)',
      border: '1px solid #ccc',
      borderRadius: '5px',
      padding: '20px',
      marginBottom: '20px',
      backgroundColor: '#f9f9f9'
    }}>
      {/* 메인 타이틀 */}
      <SectionTitle style={{ textAlign: 'center', marginBottom: '20px' }}>
        🔗 Relation Table Settings
      </SectionTitle>

      {/* 상단: 좌우 영역 */}
      <div style={{
        display: 'flex',
        gap: '20px',
        minHeight: '450px'
      }}>
        {/* 좌측: Custom Query & CTE */}
        <fieldset style={{ 
          flex: '1',
          border: '2px solid #ddd',
          borderRadius: '8px',
          padding: '16px',
          margin: 0,
          backgroundColor: 'white'
        }}>
          <legend style={{ 
            padding: '0 8px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            📝 Custom Query
          </legend>
          
          {/* 체크박스 영역 */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '12px',
            padding: '8px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #e9ecef'
          }}>
            <Checkbox
              type="checkbox"
              checked={values.useCustomQuery || false}
              onChange={(e) => setValues(prev => ({
                ...prev, 
                useCustomQuery: e.target.checked,
                selectQuery: e.target.checked ? prev.selectQuery : ''
              }))}
              style={{ marginRight: '8px' }}
            />
            <label style={{ 
              fontSize: '13px',
              color: '#495057',
              cursor: 'pointer',
              userSelect: 'none'
            }}>
              사용자 정의 쿼리 활성화
            </label>
          </div>

          {/* 쿼리 입력 영역 */}
          <textarea
            style={{ 
              width: '100%',
              height: '200px',
              fontFamily: 'Consolas, Monaco, monospace',
              fontSize: '12px',
              padding: '12px',
              margin: '0',
              boxSizing: 'border-box',
              border: values.useCustomQuery ? '2px solid #007bff' : '1px solid #ced4da',
              borderRadius: '4px',
              resize: 'none',
              backgroundColor: values.useCustomQuery ? 'white' : '#f8f9fa',
              color: values.useCustomQuery ? '#212529' : '#6c757d',
              outline: 'none',
              transition: 'border-color 0.2s ease',
              verticalAlign: 'top'
            }}
            value={values.selectQuery || ''}
            onChange={(e) => setValues(prev => ({...prev, selectQuery: e.target.value}))}
            placeholder={values.useCustomQuery ? 
              "SELECT p.title, p.content, u.name as user_name\nFROM posts p\nLEFT JOIN users u ON p.user_id = u.id\nWHERE p.status = 'active'" : 
              "사용자 정의 쿼리를 사용하려면 위의 체크박스를 선택하세요"
            }
            readOnly={!values.useCustomQuery}
          />

          {/* CTE 정의 섹션 */}
          <div style={{ marginTop: '16px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <h4 style={{ margin: 0, fontSize: '13px', color: '#495057' }}>
                <FontAwesomeIcon icon={faDatabase} /> CTE 정의
              </h4>
              <SaveButton
                onClick={handleAddCTE}
                style={{
                  backgroundColor: '#17a2b8',
                  padding: '4px 8px',
                  fontSize: '11px',
                  width: 'auto',
                  margin: 0
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> CTE 추가
              </SaveButton>
            </div>

            {cteDefinitions.map((cte, index) => (
              <div key={index} style={{
                padding: '12px',
                marginBottom: '8px',
                backgroundColor: '#e8f4f8',
                borderRadius: '4px',
                border: '1px solid #bee5eb'
              }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <Input
                    value={cte.name}
                    onChange={(e) => handleCTEChange(index, 'name', e.target.value)}
                    placeholder="CTE 이름"
                    style={{ margin: 0, fontSize: '12px', flex: '1' }}
                  />
                  <SaveButton
                    onClick={() => handleRemoveCTE(index)}
                    style={{
                      backgroundColor: '#dc3545',
                      padding: '4px 8px',
                      fontSize: '11px',
                      width: 'auto',
                      margin: 0
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </SaveButton>
                </div>
                <textarea
                  value={cte.query}
                  onChange={(e) => handleCTEChange(index, 'query', e.target.value)}
                  placeholder="CTE 쿼리 정의"
                  style={{
                    width: '100%',
                    height: '80px',
                    fontFamily: 'Consolas, Monaco, monospace',
                    fontSize: '11px',
                    padding: '8px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            ))}
          </div>
        </fieldset>

        {/* 우측: CRUD Configuration */}
        <fieldset style={{ 
          flex: '1',
          border: '2px solid #ddd',
          borderRadius: '8px',
          padding: '16px',
          margin: 0,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <legend style={{ 
            padding: '0 8px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            ⚙️ CRUD Configuration
          </legend>

          {/* 메인 테이블명 표시 (읽기 전용) */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#495057',
              marginBottom: '4px'
            }}>
              메인 테이블: <span style={{ color: '#007bff' }}>{tableName}</span>
            </label>
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#e9ecef',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#495057'
            }}>
              {tableName} (현재 게시판 테이블)
            </div>
          </div>

          {/* 테이블 선택 콤보박스 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ 
              display: 'block',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#495057',
              marginBottom: '4px'
            }}>
              테이블 추가:
            </label>
            <select
              onChange={handleTableSelectChange}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '13px',
                backgroundColor: 'white'
              }}
            >
              <option value="">테이블을 선택하세요</option>
              {availableTables.map((table, index) => {
                let tableNameOption = 'Unknown';
                try {
                  if (typeof table === 'string') {
                    tableNameOption = table;
                  } else if (table && typeof table === 'object') {
                    tableNameOption = table.table_name || table.name || table.std_group_code || 'Unknown';
                  }
                } catch (error) {
                  console.warn('Error processing table:', table);
                  tableNameOption = 'Unknown';
                }

                return (
                  <option key={`table-${index}`} value={tableNameOption}>
                    {tableNameOption}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Relation Tables 섹션 */}
          <div style={{ 
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #e9ecef',
            marginBottom: '16px'
          }}>
            <label style={{ 
              display: 'block',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#495057',
              marginBottom: '8px'
            }}>
              Relation Tables:
            </label>
            {renderInputs('save', 'relationTables')}
          </div>

          {/* keyColumn 섹션 */}
          <div style={{ 
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #e9ecef',
            flex: '1'
          }}>
            <label style={{ 
              display: 'block',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#495057',
              marginBottom: '8px'
            }}>
              Key Columns:
            </label>
            {renderInputs('save', 'keyColumn')}
          </div>
        </fieldset>
      </div>

      {/* 하단: 저장 버튼 */}
      <div style={{ marginTop: '20px' }}>
        <SaveButton primary onClick={handleSaveActionInfo} style={{ width: '100%' }}>
          <FontAwesomeIcon icon={faSave} /> Save Configuration
        </SaveButton>
      </div>

      {/* 설명 */}
      <div style={{ 
        marginTop: '16px',
        padding: '12px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#856404'
      }}>
        💡 <strong>안내:</strong> 
        메인 테이블은 현재 게시판 테이블로 자동 설정됩니다. 
        좌측에서 복잡한 조인 쿼리와 CTE를 설정하고, 우측에서 관계 테이블들과 키 컬럼을 관리하세요.
      </div>
    </div>
  );
};

export default RelationTableSettings;